import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { IconClock, IconX } from '@tabler/icons-react';
import { fetchExpenseTimeline } from '../services/reembolsosService';
import { getStatusMeta } from '../utils/reembolsosStatus';

function formatActionAtParts(iso) {
  if (!iso) return { date: '—', time: '—' };
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return { date: '—', time: '—' };
    const date = d.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
    const time = d.toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
    return { date, time };
  } catch {
    return { date: '—', time: '—' };
  }
}

/**
 * Modal de ocorrências (timeline de status da despesa).
 */
export default function GestaoReembolsosTimelineModal({
  open,
  expenseId,
  expenseMeta,
  onClose,
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [items, setItems] = useState([]);

  useEffect(() => {
    if (!open || expenseId == null) {
      setItems([]);
      setError(null);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    setItems([]);

    fetchExpenseTimeline(expenseId)
      .then((data) => {
        if (cancelled) return;
        setItems(Array.isArray(data?.items) ? data.items : []);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.message || 'Erro ao carregar ocorrências');
        setItems([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, expenseId]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const subtitleParts = [
    expenseMeta?.code ? `Código ${expenseMeta.code}` : null,
    expenseMeta?.amountLabel ? `R$ ${expenseMeta.amountLabel}` : null,
  ].filter(Boolean);

  return ReactDOM.createPortal(
    <div className="gr-oc-modal" role="dialog" aria-modal="true" aria-label="Ocorrências">
      <div className="gr-oc-modal-backdrop" onClick={() => onClose?.()} />
      <div className="gr-oc-modal-panel">
        <header className="gr-oc-modal-head">
          <div className="gr-oc-modal-head-text">
            <h3 className="gr-oc-modal-title">
              <IconClock size={18} stroke={1.75} aria-hidden />
              Ocorrências
            </h3>
            {subtitleParts.length ? (
              <p className="gr-oc-modal-sub">{subtitleParts.join(' · ')}</p>
            ) : null}
          </div>
          <button
            type="button"
            className="gr-oc-modal-close"
            onClick={() => onClose?.()}
            aria-label="Fechar"
          >
            <IconX size={18} stroke={1.75} />
          </button>
        </header>

        <div className="gr-oc-modal-body">
          {loading ? (
            <div className="gr-oc-state">Carregando ocorrências…</div>
          ) : null}
          {!loading && error ? (
            <div className="gr-oc-state is-error" role="alert">
              {error}
            </div>
          ) : null}
          {!loading && !error && !items.length ? (
            <div className="gr-oc-state">Nenhuma ocorrência registrada.</div>
          ) : null}
          {!loading && !error && items.length ? (
            <>
              <div className="gr-oc-table-head" aria-hidden>
                <span className="gr-oc-col-id">#</span>
                <span className="gr-oc-col-date">Data</span>
                <span className="gr-oc-col-time">Hora</span>
                <span className="gr-oc-col-status">Status</span>
                <span className="gr-oc-col-user">Usuário</span>
                <span className="gr-oc-col-reason">Motivo</span>
              </div>
              <div className="gr-oc-list" role="list">
                {items.map((item) => {
                  const parts = formatActionAtParts(item.actionAt);
                  const st = getStatusMeta(item.statusKey);
                  const label = item.statusLabel || st.label;
                  return (
                    <div
                      key={item.id || `${item.displayIndex}-${item.actionAt}`}
                      className="gr-oc-row"
                      role="listitem"
                    >
                      <span className="gr-oc-id" title={`#${item.displayIndex}`}>
                        {item.displayIndex}
                      </span>
                      <span className="gr-oc-field gr-oc-date">{parts.date}</span>
                      <span className="gr-oc-field gr-oc-time">{parts.time}</span>
                      <span className="gr-oc-field gr-oc-status">
                        <span className="gr-status-pill is-plain" style={{ color: st.fg }}>
                          <span className="gr-status-dot" style={{ background: st.dot }} />
                          {label}
                        </span>
                      </span>
                      <span className="gr-oc-field gr-oc-user" title={item.userName || ''}>
                        {item.userName || '—'}
                      </span>
                      <span
                        className={`gr-oc-field gr-oc-reason${item.rejectReason ? '' : ' is-empty'}`}
                        title={item.rejectReason || ''}
                      >
                        {item.rejectReason || '—'}
                      </span>
                    </div>
                  );
                })}
              </div>
            </>
          ) : null}
        </div>
      </div>
    </div>,
    document.body
  );
}
